<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-12">
        <div v-if="loading" class="text-center text-muted mb-4">
          <font-awesome-icon icon="spinner" spin class="mr-2"/>
          Loading content details...
        </div>
        <div v-else-if="error" class="text-danger text-center mb-4">
          {{ error }}
        </div>
        <div v-else-if="contentShares.length > 0">
          <div v-for="contentShare in contentShares" :key="contentShare.id" class="row justify-content-center mb-4">
            <div class="col-12 col-md-8 col-lg-6">
              <div class="card">
                <div class="card-body">
                  <Share
                    :url="getContentShareUrl(contentShare)"
                    :title="getContentTitle(contentShare)"
                    :preview-text="shareText"
                    :preview-args="{ contentTitle: contentShare.title }"
                    :providers="canShareJobsWith"
                    :source="'content'"
                    :post-type="getPostType(contentShare)"
                    :centered="true"
                    @track="(data) => trackContentSharedEvent(data, contentShare)"
                    @openLinkedInConnectionModal="() => handleLinkedInConnectionModal(contentShare.id)"
                    @openLinkedInPostModal="() => handleLinkedInPostModal(contentShare.id)"
                  >
                    <template #text>
                      <div class="mb-3">
                        <div v-if="contentShare.image && contentShare.image.fileLocation" class="mb-3">
                          <img :src="contentShare.image.fileLocation" :alt="contentShare.title"
                               class="w-100 rounded" style="max-height: 200px; object-fit: cover;">
                        </div>
                        <div v-else-if="contentShare.imageURL" class="mb-3">
                          <img :src="contentShare.imageURL" :alt="contentShare.title"
                               class="w-100 rounded" style="max-height: 200px; object-fit: cover;">
                        </div>

                        <h5 class="mb-2 d-none">{{ contentShare.title }}</h5>
                        <p v-if="company" class="text-muted">{{ company.name }}</p>
                        <p v-if="contentShare.description" class="text-muted">{{ contentShare.description }}</p>
                      </div>
                    </template>
                  </Share>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Connect LinkedIn Modals -->
    <b-modal v-for="contentShare in contentShares" :key="`connect-${contentShare.id}`" v-if="hasContentSharesV2Enabled" title="Connect LinkedIn" :id="`connect-linkedin-${contentShare.id}`" hide-footer @close="cleanupRoute()">
      <ConnectLinkedIn
        :entryID="contentShare.id"
      />
    </b-modal>

    <!-- LinkedIn Post Editor Modals -->
    <b-modal v-for="contentShare in contentShares" :key="`editor-${contentShare.id}`" v-if="hasContentSharesV2Enabled" :id="`content-share-linkedin-post-editor-${contentShare.id}`" hide-footer title="Edit LinkedIn Post" no-close-on-backdrop no-close-on-esc @close="cleanupRoute()">
      <LinkedInPostEditor
        :contentShare="contentShare"
        :campaignID="contentShare && contentShare.campaigns && contentShare.campaigns[0] && contentShare.campaigns[0].id"
        @track="(data) => trackContentSharedEvent(data, contentShare)"
      />
    </b-modal>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import Share from '@/components/Share.vue';
import LinkedInPostEditor from '@/components/content-share/LinkedInPostEditor';
import ConnectLinkedIn from '@/components/content-share/ConnectLinkedIn';
import { mapRoot, mapPreferences } from '@src/mixins/root';

const { default: confetti } = process.env.VUE_ENV === 'client' ? require('canvas-confetti') : null;

const GET_CONTENT_SHARES_QUERY = gql`
  query GetContentShares($contentShareIDs: [ID!]!) {
    company {
      id
      name
      contentShares(where: { id_in: $contentShareIDs }) {
        id
        title
        description
        content
        image {
          id
          fileLocation
        }
        imageURL
        url
        shareCode
        campaigns {
          id
          title
        }
      }
    }
  }
`;

export default {
  name: 'EngagementShareContent',
  components: {
    Share,
    LinkedInPostEditor,
    ConnectLinkedIn,
  },
  props: {
    contentShareIDs: {
      type: String,
      required: true,
    },
  },
  mixins: [
    mapRoot([ 'company', 'user', 'origin', 'isAssurant', 'isHelloFresh' ])
  ],
  data() {
    return {
      error: null,
    };
  },
  async mounted() {
    const { state } = this.user || {};
    const { linkedIn } = state || {};
    const { accessToken, expiresAt, version } = linkedIn || {};
    const now = new Date().getTime();
    const isLinkedInConnected = accessToken && expiresAt && expiresAt > now && version === 'v1';

    if (this.$route.query && this.$route.query.c && this.contentShareIDArray.includes(this.$route.query.c)) {
      if (isLinkedInConnected) {
        this.$bvModal.show(`content-share-linkedin-post-editor-${this.$route.query.c}`);
      } else {
        this.$bvModal.show(`connect-linkedin-${this.$route.query.c}`);
      }
    }

    if (this.$route.query && this.$route.query.callback && !this.$route.hash.includes('error')) {
      const contentShareID = this.$route.query.callback.split(':')[1];

      if (contentShareID && this.contentShareIDArray.includes(contentShareID)) {
        this.$bvModal.show(`content-share-linkedin-post-editor-${contentShareID}`);
      }
    }
  },
  events: {
    capturedLog({ eventName }) {
      if ([ 'JOB_SHARED', 'CONTENT_SHARED' ].includes(eventName)) {
        confetti({ particleCount: 150, spread: 180, zIndex: 1200 });
      }
    },
  },
  apollo: {
    contentSharesData: {
      query: GET_CONTENT_SHARES_QUERY,
      variables() {
        return { contentShareIDs: this.contentShareIDArray };
      },
      update: ({ company }) => company,
      error(err) {
        console.error('Error loading content shares:', err);
        this.error = 'Failed to load content details';
      },
    },
  },
  computed: {
    contentShareIDArray() {
      return this.contentShareIDs.split(',').filter(id => id.trim());
    },
    loading() {
      return this.$apollo.queries.contentSharesData.loading;
    },
    contentShares() {
      return this.contentSharesData && this.contentSharesData.contentShares ? this.contentSharesData.contentShares : [];
    },
    company() {
      return this.contentSharesData || null;
    },
    ...mapRoot({
      canShareJobsWith({ company }) {
        const { preferences, sitePreferences, sharePreferences } = company || {};
        const { canShareJobsWith } = { ...preferences, ...sitePreferences, ...sharePreferences };
        return canShareJobsWith;
      },
      shareText({ company }) {
        const { preferences, sitePreferences, ...sharePreferences } = company || {};
        const { customFields } = this.contentShares[0] || {};
        const { shareText } = { ...preferences, ...sitePreferences, ...sharePreferences, ...customFields };

        if (this.isAssurant && this.user && this.user.locale === 'fr') {
          return 'Découvrez ce contenu intéressant: {contentTitle}';
        }

        if (this.isHelloFresh && this.user && this.user.locale === 'de') {
          return 'Schau dir das mal an: {contentTitle}';
        }

        return shareText || 'Check out this content: {contentTitle}';
      },
    }),
    ...mapPreferences([ 'featuresEnabled' ]),
    hasContentSharesV2Enabled() {
      return (this.featuresEnabled || []).includes('content-share-v2');
    },
  },
  methods: {
    getContentShareUrl(contentShare) {
      return `${window.location.origin}/content-share/${contentShare.shareCode}`;
    },
    getContentTitle(contentShare) {
      return `${this.$t('contentShares.card.share-cta')}: ${contentShare.title}`;
    },
    getPostType(contentShare) {
      if (contentShare.image || contentShare.imageURL) return 'image';
      if (contentShare.video) return 'video';
      if (contentShare.document) return 'document';
      return 'link';
    },
    handleLinkedInConnectionModal(contentShareID) {
      this.$bvModal.show(`connect-linkedin-${contentShareID}`);
    },
    handleLinkedInPostModal(contentShareID) {
      this.$bvModal.show(`content-share-linkedin-post-editor-${contentShareID}`);
    },
    async trackContentSharedEvent({ channel }, contentShare) {
      try {
        const { id: contentShareID, campaigns } = contentShare || {};
        const { id: campaignID } = (campaigns && campaigns[0]) || {};
        await this.$root.captureLog({
          eventName: 'CONTENT_SHARED',
          campaign: campaignID ? { id: campaignID } : undefined,
          contentShare: { id: contentShareID },
          user: { self: true },
          dataHash: { channel },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    cleanupRoute() {
      if (this.$router.history && this.$router.history.current && this.$router.history.current.query) {
        // refresh the page if we open the card from a comm message
        if (this.$router.history.current.query.c || (this.$router.history.current.query.callback && this.$router.history.current.query.callback.startsWith('connectLinkedInForPosting'))) {
          this.$router.push(this.$router.history.current.path);
          location.reload();
        } else {
          this.$router.push(this.$router.history.current.path);
        }
      }
    }
  },
};
</script>

<style scoped>
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: none;
}

.card-body {
  padding: 2rem;
}
</style>



// WEBPACK FOOTER //
// src/views/engagement/ShareContent.vue